/**
 * The CSS selector for "an element a focus trap would consider a candidate".
 *
 * Shared by `useFocusTrap` (`@/hooks/use-focus-trap`), which uses it to pick
 * the element to focus when a modal opens, and by `ConfirmationDialog`, which
 * uses it to ask whether the dialog's body already offers a focus target
 * before its cancel-focus timer fires (#13990).
 *
 * ## Why this lives in its own module
 *
 * Those two callers have to agree. If the trap gains a candidate type this
 * string lacks — `[contenteditable]` is the obvious one — the trap would focus
 * a body control that `ConfirmationDialog` cannot see, and the timer would pull
 * focus back to Cancel: exactly the defect #13990 fixed, reintroduced silently
 * and with no test to catch it.
 *
 * Exporting a second symbol from `@/hooks/use-focus-trap` would have been the
 * obvious way to share it, and it does not work here: 21 test files
 * `vi.mock('@/hooks/use-focus-trap', …)` with a factory that exports
 * `useFocusTrap` alone, and vitest hard-errors on a missing export of a mocked
 * module. A third module is unaffected by those mocks — `vi.mock` is keyed on
 * the module specifier, not on the transitive import graph — so both callers
 * can import from here while the mocks keep replacing only the hook.
 *
 * ## What it does NOT encode
 *
 * Visibility. The trap additionally filters its matches by
 * `offsetParent !== null` to pick an element that is actually rendered; this
 * selector answers the narrower question of what the markup contains. Callers
 * that need the visibility half apply it themselves.
 *
 * The `:not([disabled])` clauses are load-bearing: a disabled control is not a
 * focus target, and treating one as though it were would leave a dialog opening
 * with no initial focus in any environment where the trap finds nothing else.
 *
 * Three drawer components (`performance-review-drawer`, `post-update-drawer`,
 * `contractor-check-in-drawer`) carry their own, slightly different, inlined
 * selector. They are deliberately out of scope here: they trap focus in their
 * own containers and nothing cross-checks them against this list, so folding
 * them in is a separate change with its own regression surface.
 */
export const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')
